/**
 * Configuración: organización, datos de pago, chips NFC e integraciones.
 *
 * **Nada de configuración de negocio vive en el código** (§91): la cuenta de
 * SINPE, los datos bancarios y el nombre de la organización se cambian acá.
 *
 * «Chip NFC», no «etiqueta» (CB-22): el objeto físico es un disco rígido tipo
 * moneda pegado al consultorio, no una etiqueta colgante. El renombre es SOLO
 * de interfaz — la tabla `nfc_tokens` y sus endpoints conservan su nombre: un
 * cambio de vocabulario visible no justifica una migración.
 */

import { useId, useState } from 'react';
import { api } from '@/lib/api';
import { useApi, useApiMutation } from '@/lib/hooks';
import { haptic } from '@/lib/haptics'; // HAPTIC_FEEDBACK
import { IconNfc } from '@/components/common/NavIcons';
import {
  Async, Badge, Button, Card, Cell, Empty, Field, Modal, Note, PageHead, Table,
} from '@/components/ui';

interface Settings {
  organization: {
    id: string; name: string; legal_name: string | null; tax_id: string | null;
    unit_label: string; unit_label_plural: string; default_tax_rate: number; currency: string;
  };
  settings: Record<string, unknown>;
}

interface NfcToken {
  id: string; token_prefix: string; label: string | null; is_active: number;
  scan_count: number; last_scanned_at: string | null; unit_code: string | null; unit_name: string | null;
}

export function SettingsPage() {
  const [tab, setTab] = useState<'org' | 'payment' | 'payroll' | 'nfc' | 'integrations'>('org');
  const settings = useApi<Settings>('/admin/settings');
  const tokens = useApi<{ tokens: NfcToken[] }>('/admin/nfc-tokens', { enabled: tab === 'nfc' });

  return (
    <>
      <PageHead title="Configuración" subtitle="Los datos del negocio. Nada de esto está escrito en el código." />

      <div className="tabs mb-2">
        {([['org', 'Organización'], ['payment', 'Datos de pago'], ['payroll', 'Planilla'],
          ['nfc', 'Chips NFC'], ['integrations', 'Integraciones']] as const)
          // HAPTIC_FEEDBACK: light tap on tab switch, in the onClick below
          .map(([key, label]) => (
            <button key={key} type="button" className={`tab${tab === key ? ' is-active' : ''}`} aria-pressed={tab === key}
              onClick={() => { if (tab !== key) { haptic.light(); setTab(key); } }}>{label}</button>
          ))}
      </div>

      {tab === 'org' && (
        <Async query={settings}>{(data) => <OrganizationForm org={data.organization} />}</Async>
      )}

      {tab === 'payment' && (
        <Async query={settings}>{(data) => <PaymentSettings settings={data.settings} />}</Async>
      )}

      {tab === 'payroll' && (
        <Async query={settings}>{(data) => <PayrollSettings settings={data.settings} />}</Async>
      )}

      {tab === 'nfc' && <NfcTokens query={tokens} />}

      {tab === 'integrations' && <Integrations />}
    </>
  );
}

function OrganizationForm({ org }: { org: Settings['organization'] }) {
  const [form, setForm] = useState({
    name: org.name, legal_name: org.legal_name ?? '', tax_id: org.tax_id ?? '',
    unit_label: org.unit_label, unit_label_plural: org.unit_label_plural,
  });

  const mutation = useApiMutation(
    () => api.patch('/admin/organization', form),
    ['/admin/settings'],
  );

  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <Card
      title="Organización"
      actions={
        <Button variant="primary" disabled={mutation.isPending} onClick={() => mutation.mutate()}>
          {mutation.isPending ? 'Guardando…' : 'Guardar'}
        </Button>
      }
    >
      {mutation.error && <Note tone="danger">{mutation.error.message}</Note>}
      {mutation.isSuccess && <Note tone="ok">Guardado.</Note>}

      <div className="grid grid--2">
        <Field label="Nombre"><input className="input" value={form.name} onChange={set('name')} /></Field>
        <Field label="Razón social" hint="La que va en la factura electrónica.">
          <input className="input" value={form.legal_name} onChange={set('legal_name')} />
        </Field>
      </div>
      <Field label="Cédula jurídica">
        <input className="input mono" value={form.tax_id} onChange={set('tax_id')} />
      </Field>

      <Note tone="info">
        Cómo se llama la unidad que se atiende. Hoy es «Consultorio»; si mañana entran oficinas se
        cambia acá y toda la interfaz sigue.
      </Note>
      <div className="grid grid--2 mt-2">
        <Field label="Singular"><input className="input" value={form.unit_label} onChange={set('unit_label')} /></Field>
        <Field label="Plural"><input className="input" value={form.unit_label_plural} onChange={set('unit_label_plural')} /></Field>
      </div>
    </Card>
  );
}

function PaymentSettings({ settings }: { settings: Record<string, unknown> }) {
  const current = (settings.payment_info ?? {}) as Record<string, string>;
  const [form, setForm] = useState({
    sinpe_phone: current.sinpe_phone ?? '',
    sinpe_name: current.sinpe_name ?? '',
    bank_name: current.bank_name ?? '',
    bank_account: current.bank_account ?? '',
    bank_iban: current.bank_iban ?? '',
  });

  const mutation = useApiMutation(
    () => api.put('/admin/settings/payment_info', {
      value: form,
      description: 'Datos que ve el cliente para pagar',
    }),
    ['/admin/settings'],
  );

  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <Card
      title="Datos de pago"
      actions={
        <Button variant="primary" disabled={mutation.isPending} onClick={() => mutation.mutate()}>
          Guardar
        </Button>
      }
    >
      {mutation.isSuccess && <Note tone="ok">Guardado.</Note>}
      <Note tone="info">Esto es lo que el cliente ve en su portal a la hora de pagar.</Note>

      <div className="grid grid--2 mt-2">
        <Field label="SINPE Móvil"><input className="input" value={form.sinpe_phone} onChange={set('sinpe_phone')} /></Field>
        <Field label="A nombre de"><input className="input" value={form.sinpe_name} onChange={set('sinpe_name')} /></Field>
      </div>
      <div className="grid grid--2">
        <Field label="Banco"><input className="input" value={form.bank_name} onChange={set('bank_name')} /></Field>
        <Field label="Cuenta"><input className="input mono" value={form.bank_account} onChange={set('bank_account')} /></Field>
      </div>
      <Field label="IBAN"><input className="input mono" value={form.bank_iban} onChange={set('bank_iban')} /></Field>
    </Card>
  );
}

/* ── Planilla ─────────────────────────────────────────────────────────────── */

/**
 * Los porcentajes de la planilla: dos cargas sociales y tres provisiones.
 *
 * Existen acá porque la pestaña de Planilla dice que se editan acá, y porque
 * **no pueden vivir en el código** (§91): cambian por decreto, y el régimen de
 * cesantía varía de empresa a empresa. Cada corrida los congela, así que
 * cambiarlos no toca ninguna planilla ya asentada.
 *
 * Se escriben como PORCENTAJE —26,67— y se guardan como fracción —0,2667—,
 * porque nadie piensa en fracciones y porque un 26,67 guardado tal cual
 * multiplicaría la planilla por cien. El Worker rechaza cualquier valor fuera
 * de `[0, 1)` de todos modos: acá se convierte, allá se verifica.
 */
const PAYROLL_FIELDS = [
  ['employer_rate', 'Carga patronal', 'La paga la empresa por encima del salario.'],
  ['worker_rate', 'Carga obrera', 'Se le retiene al trabajador de su bruto.'],
  ['christmas_bonus_rate', 'Aguinaldo', 'Un mes de salario al año = 8,33 %. Se paga en diciembre.'],
  ['severance_rate', 'Cesantía', 'Varía según el régimen. Confirmalo con tu contador.'],
  ['vacation_rate', 'Vacaciones', 'Dos semanas al año = medio mes = 4,16 %.'],
] as const;

function PayrollSettings({ settings }: { settings: Record<string, unknown> }) {
  const current = (settings.payroll ?? {}) as Record<string, number | undefined>;
  const [form, setForm] = useState(() => Object.fromEntries(
    PAYROLL_FIELDS.map(([key]) => [key, pctOf(current[key])]),
  ) as Record<string, string>);

  const parsed = PAYROLL_FIELDS.map(([key]) => [key, fractionOf(form[key] ?? '')] as const);
  const invalid = parsed.filter(([, v]) => v === null).map(([k]) => k);

  const mutation = useApiMutation(
    () => api.put('/admin/settings/payroll', {
      value: Object.fromEntries(parsed.map(([k, v]) => [k, v])),
      description: 'Cargas sociales y provisiones de planilla. Confirmalas con tu contador.',
    }),
    ['/admin/settings', '/admin/payroll'],
  );

  return (
    <Card
      title="Cargas sociales y provisiones"
      actions={
        <Button variant="primary" disabled={invalid.length > 0 || mutation.isPending}
          onClick={() => mutation.mutate()}>
          {mutation.isPending ? 'Guardando…' : 'Guardar'}
        </Button>
      }
    >
      {mutation.error && <Note tone="danger">{mutation.error.message}</Note>}
      {mutation.isSuccess && <Note tone="ok">Guardado.</Note>}

      <Note tone="warn">
        Estos números vienen de referencia y <strong>tenés que confirmarlos con tu
        contador</strong>. Cambiarlos afecta las planillas que corras de acá en
        adelante: las ya corridas conservan los porcentajes con que se calcularon.
      </Note>

      {invalid.length === PAYROLL_FIELDS.length && (
        <Note tone="info">
          Todavía no hay porcentajes guardados para esta organización. Escribilos
          y guardá; mientras tanto la planilla usa los valores de referencia.
        </Note>
      )}

      <div className="grid grid--2 mt-2">
        {PAYROLL_FIELDS.map(([key, label, hint]) => (
          <Field
            key={key}
            label={`${label} (%)`}
            hint={hint}
            // El error aparece cuando hay algo escrito y está mal, no sobre un
            // campo que todavía nadie tocó.
            error={form[key] && fractionOf(form[key] ?? '') === null
              ? 'Un porcentaje entre 0 y 100 (26,67 se escribe 26.67).' : undefined}
          >
            <input
              className="input"
              inputMode="decimal"
              value={form[key] ?? ''}
              onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
            />
          </Field>
        ))}
      </div>
    </Card>
  );
}

/** `0.2667` → `"26.67"`. Vacío si la clave todavía no existe. */
function pctOf(fraction: number | undefined): string {
  return fraction == null ? '' : String(Number((fraction * 100).toFixed(4)));
}

/** `"26,67"` → `0.2667`. `null` si no es un porcentaje válido. */
function fractionOf(raw: string): number | null {
  const n = Number.parseFloat(raw.replace(',', '.'));
  if (!Number.isFinite(n) || n < 0 || n >= 100) return null;
  // Cuatro decimales de fracción: `26.67 %` → `0.2667`, sin cola binaria.
  return Number((n / 100).toFixed(6));
}

/**
 * El reverso del chip real, generado por código (CB-22c, el Easter egg 🥚).
 *
 * El chip físico es una moneda: navy profundo con olas azules y el número del
 * consultorio en blanco, grande, centrado. Acá la columna «Consultorio» muestra
 * esa cara en vez del número pelado. Paleta muestreada del archivo del dueño
 * (anexo A-CB-22 bis): olas en `--blue-700` / `--blue-600` / `--brand`, y la
 * base en el navy profundo del degradado de `.auth-stage` (#1b2338) — es MÁS
 * oscura que `--navy` a propósito; con `--navy` la moneda queda lavada. El
 * logo de la cara real se omite: a 44px sería una mancha.
 *
 * **El número es texto del DOM encima del disco, no un glifo del SVG.** Es lo
 * que lo deja seleccionable, buscable con Ctrl+F y legible para un lector de
 * pantalla; el disco es decoración (`aria-hidden`).
 *
 * `Sin consultorio` es un estado real (chips emitidos sin asignar): la misma
 * moneda sin número, atenuada — en blanco parecería un dato que no cargó.
 */
function NfcCoin({ code }: { code: string | null }) {
  // Un id por instancia: hay una moneda por fila y los ids del SVG son
  // globales al documento — siete `clipPath` con el mismo id es HTML inválido
  // aunque los navegadores lo perdonen.
  const clipId = useId();
  return (
    <span className={`nfc-coin${code == null ? ' nfc-coin--blank' : ''}`}>
      <svg viewBox="0 0 48 48" aria-hidden="true">
        <defs>
          <clipPath id={clipId}><circle cx="24" cy="24" r="22" /></clipPath>
        </defs>
        <circle cx="24" cy="24" r="22" fill="#1b2338" />
        <g clipPath={`url(#${clipId})`}>
          {/* Las olas no son simétricas, como en la pieza real: la de arriba
              entra por la derecha y baja; las de abajo suben desde la
              izquierda con menos opacidad. */}
          <path d="M18 -2 C30 6 38 2 50 12 L50 -2 Z" fill="var(--blue-600)" opacity=".95" />
          <path d="M26 -2 C36 8 44 4 50 10 L50 -2 Z" fill="var(--brand)" opacity=".55" />
          <path d="M-2 36 C10 30 18 40 26 50 L-2 50 Z" fill="var(--blue-700)" opacity=".9" />
          <path d="M-2 42 C8 37 14 44 20 50 L-2 50 Z" fill="var(--brand)" opacity=".4" />
        </g>
        {/* El canto: es lo que hace leer la pieza como moneda con grosor. */}
        <circle cx="24" cy="24" r="22" fill="none" stroke="var(--navy)" strokeWidth="1.6" />
        <circle cx="24" cy="24" r="19.8" fill="none" stroke="rgba(255,255,255,.14)" strokeWidth=".9" />
      </svg>
      {code != null && <span className="nfc-coin__num">{code}</span>}
    </span>
  );
}

function NfcTokens({ query }: { query: ReturnType<typeof useApi<{ tokens: NfcToken[] }>> }) {
  const [issued, setIssued] = useState<{ url: string } | null>(null);
  const units = useApi<{ units: { id: string; unit_code: string }[] }>('/admin/units');
  const [unitId, setUnitId] = useState('');

  const create = useApiMutation(
    () => api.post<{ url: string }>('/admin/nfc-tokens', { service_unit_id: unitId || undefined }),
    ['/admin/nfc-tokens'],
    { onSuccess: (data) => setIssued(data) },
  );

  const revoke = useApiMutation(
    (id: string) => api.post(`/admin/nfc-tokens/${id}/revoke`),
    ['/admin/nfc-tokens'],
  );

  const remove = useApiMutation(
    (id: string) => api.post(`/admin/nfc-tokens/${id}/delete`),
    ['/admin/nfc-tokens'],
  );

  return (
    <>
      <Card
        title={<h2 className="row"><IconNfc />Chips NFC</h2>}
        flush
        actions={
          <div className="row">
            <select className="select" value={unitId} onChange={(e) => setUnitId(e.target.value)}>
              <option value="">Sin consultorio</option>
              {(units.data?.units ?? []).map((u) => (
                <option key={u.id} value={u.id}>{u.unit_code}</option>
              ))}
            </select>
            <Button variant="primary" disabled={create.isPending} onClick={() => create.mutate()}>
              Emitir chip
            </Button>
          </div>
        }
      >
        <Async query={query} empty={(d) => d.tokens.length === 0 && (
          <Empty icon="solicitudes" title="Sin chips emitidos" hint="Cada consultorio puede tener el suyo." />
        )}>
          {(data) => (
            /* Orden pedido por el dueño (CB-22d): primero el consultorio, que
               es como uno busca el chip; después el chip con espacio para que
               el prefijo se lea entero. Antes era al revés: el código en una
               columna angosta y tres dígitos ocupando la ancha. */
            <Table head={
              <tr>
                <th>Consultorio</th><th>Chip</th>
                <th className="right">Escaneos</th><th>Estado</th><th />
              </tr>
            }>
              {data.tokens.map((t) => (
                <tr key={t.id}>
                  <td>
                    <span className="nfc-coin-cell">
                      <NfcCoin code={t.unit_code} />
                      <Cell sub={t.unit_name}>
                        {t.unit_code == null && <span className="muted">Sin consultorio</span>}
                      </Cell>
                    </span>
                  </td>
                  {/* El prefijo completo, sin `…`: son los 6 primeros
                      caracteres y es TODO lo que el sistema tiene — el token
                      se guarda hasheado (migración 0007) y el texto plano
                      solo existe en el chip físico y en su URL. El espacio
                      extra es para leerlo entero, no para revelar más. */}
                  <td className="mono">
                    <Cell sub={t.label}>{t.token_prefix}</Cell>
                  </td>
                  <td className="right">
                    <Cell sub={t.last_scanned_at?.slice(0, 10)}>{t.scan_count}</Cell>
                  </td>
                  <td>
                    {t.is_active ? <Badge tone="ok">Activo</Badge> : <Badge tone="neutral">Revocado</Badge>}
                  </td>
                  <td className="right">
                    {t.is_active === 1 ? (
                      <Button variant="danger" disabled={revoke.isPending} onClick={() => revoke.mutate(t.id)}>
                        Revocar
                      </Button>
                    ) : (
                      <Button
                        variant="danger"
                        disabled={remove.isPending}
                        onClick={() => {
                          if (window.confirm('¿Eliminar este chip revocado? No se puede deshacer.')) {
                            remove.mutate(t.id);
                          }
                        }}
                      >
                        Eliminar
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </Table>
          )}
        </Async>
      </Card>

      {issued && (
        <Modal
          title="Chip emitido"
          onClose={() => setIssued(null)}
          footer={<Button variant="primary" onClick={() => setIssued(null)}>Listo</Button>}
        >
          <Note tone="warn">
            Guardá esta dirección ahora: es la única vez que se muestra. La base solo conserva su
            huella, así que ni nosotros podemos recuperarla después.
          </Note>
          <div className="row mt-2">
            <input className="input mono" readOnly value={issued.url} onFocus={(e) => e.target.select()} />
            <Button onClick={() => void navigator.clipboard.writeText(issued.url)}>Copiar</Button>
          </div>
        </Modal>
      )}
    </>
  );
}

function Integrations() {
  const query = useApi<{
    endpoints: { id: string; code: string; url: string; event_types: string[]; is_active: number }[];
    deliveries: {
      id: string; event_type: string; status: string; attempt_count: number;
      last_status_code: number | null; last_error: string | null; created_at: string; endpoint_code: string;
    }[];
  }>('/admin/webhooks');

  const dispatch = useApiMutation(() => api.post('/admin/webhooks/dispatch'), ['/admin/webhooks']);

  return (
    <Async query={query}>
      {(data) => (
        <div style={{ display: 'grid', gap: 14 }}>
          <Card title="Destinos configurados" flush>
            {data.endpoints.length === 0 ? (
              <Empty icon="intercambio" title="Sin integraciones" hint="Acá se configura a dónde avisar cuando pasa algo." />
            ) : (
              <Table head={<tr><th>Código</th><th>URL</th><th>Eventos</th><th>Estado</th></tr>}>
                {data.endpoints.map((e) => (
                  <tr key={e.id}>
                    <td className="strong">{e.code}</td>
                    <td className="mono small">{e.url}</td>
                    <td className="small muted">{e.event_types.join(', ')}</td>
                    <td>{e.is_active ? <Badge tone="ok">Activo</Badge> : <Badge tone="neutral">Inactivo</Badge>}</td>
                  </tr>
                ))}
              </Table>
            )}
          </Card>

          <Card
            title="Últimos envíos"
            flush
            actions={
              <Button disabled={dispatch.isPending} onClick={() => dispatch.mutate()}>
                Reintentar pendientes
              </Button>
            }
          >
            {data.deliveries.length === 0 ? (
              <Empty icon="comunicacion" title="Todavía no se envió nada" />
            ) : (
              <Table head={<tr><th>Evento</th><th>Destino</th><th className="right">Intentos</th><th>Estado</th></tr>}>
                {data.deliveries.map((d) => (
                  <tr key={d.id}>
                    <td><Cell sub={d.created_at.slice(0, 16).replace('T', ' ')}>{d.event_type}</Cell></td>
                    <td className="small">{d.endpoint_code}</td>
                    <td className="right">{d.attempt_count}</td>
                    <td>
                      <Badge tone={d.status === 'SUCCEEDED' ? 'ok' : d.status === 'DEAD' ? 'danger' : 'warn'}>
                        {d.status}
                      </Badge>
                      {d.last_error && <span className="tbl__sub">{d.last_error.slice(0, 80)}</span>}
                    </td>
                  </tr>
                ))}
              </Table>
            )}
          </Card>
        </div>
      )}
    </Async>
  );
}
